// Social Lite → Templates drawer: connect Creatomate master templates per content type × creative mode.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Loader2, Check, Link2, Zap, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SocialLiteTemplatesDrawer({ meta, onClose, onChanged }) {
  const [saving, setSaving] = useState(null);
  const [adding, setAdding] = useState(null); // {content_type, creative_mode}
  const [newName, setNewName] = useState("");

  const refresh = async () => { const m = await axios.get(`${API}/social-lite/meta`); onChanged(m.data); };
  const save = async (t, changes) => {
    setSaving(t.template_id);
    try { await axios.put(`${API}/social-lite/templates/${t.template_id}`, { changes }); await refresh(); toast.success(`${t.template_name} saved`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(null); }
  };
  const add = async () => {
    if (!newName.trim()) return;
    try { await axios.post(`${API}/social-lite/templates`, { ...adding, template_name: newName.trim() }); await refresh(); setAdding(null); setNewName(""); toast.success("Template added — paste its Creatomate ID to connect it"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't add"); }
  };

  return (
    <>
      <div className="slt-drawer-backdrop" onClick={onClose} />
      <aside className="slt-drawer" data-testid="social-lite-templates-drawer">
        <div className="slt-drawer-head">
          <div><h3>Social Lite templates</h3><p>One Creatomate master per content type × creative mode. Positions fill Image_01, Image_02… in order; the template owns the motion.</p></div>
          <button className="slt-drawer-close" onClick={onClose} aria-label="Close" data-testid="social-lite-templates-close"><X size={18} /></button>
        </div>
        <div className="slt-drawer-body">
          {meta.content_types.map((c) => (
            <div key={c.key} className="slt-tpl-group" data-testid={`social-lite-tpl-group-${c.key}`}>
              <h4>{c.label}</h4>
              {meta.creative_modes.map((m) => {
                const Icon = m.key === "dynamic" ? Zap : Layers;
                const list = meta.templates.filter((t) => t.content_type === c.key && t.creative_mode === m.key);
                return (
                  <div key={m.key} className="slt-tpl-mode">
                    <div className="slt-tpl-mode-head"><Icon size={13} /> {m.label}
                      <button className="slt-mini" onClick={() => { setAdding({ content_type: c.key, creative_mode: m.key }); setNewName(`Social Lite ${m.label} ${String(list.length + 1).padStart(2, "0")} — ${c.label}`); }} data-testid={`social-lite-tpl-add-${c.key}-${m.key}`}><Plus size={12} /> Add</button>
                    </div>
                    {adding && adding.content_type === c.key && adding.creative_mode === m.key && (
                      <div className="slt-tpl-add" data-testid="social-lite-tpl-add-form">
                        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Template name" data-testid="social-lite-tpl-add-name" />
                        <button className="slt-mini is-primary" onClick={add} data-testid="social-lite-tpl-add-save"><Check size={12} /> Create</button>
                        <button className="slt-mini" onClick={() => setAdding(null)}>Cancel</button>
                      </div>
                    )}
                    {list.map((t) => <TemplateCard key={t.template_id} t={t} saving={saving === t.template_id} onSave={(changes) => save(t, changes)} />)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}

function TemplateCard({ t, saving, onSave }) {
  const [form, setForm] = useState({
    template_name: t.template_name, creatomate_template_id: t.creatomate_template_id || "", minimum_images: t.minimum_images,
    recommended_images: t.recommended_images, maximum_images: t.maximum_images, slot_prefix: (t.image_slot_names[0] || "Image_01").replace(/\d+$/, ""),
    music_enabled: t.music_enabled, beat_sync_enabled: t.beat_sync_enabled, default_duration: t.default_duration, active: t.active !== false,
    unused_slots: t.unused_slots || "trim", fit_to_length: t.fit_to_length !== false,
    preview_image: t.preview_image || "", preview_video: t.preview_video || "", description: t.description || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => Math.max(1, parseInt(v || "1", 10) || 1);
  const submit = () => {
    const maximum = num(form.maximum_images);
    onSave({
      template_name: form.template_name, creatomate_template_id: form.creatomate_template_id.trim() || null, description: form.description,
      minimum_images: num(form.minimum_images), recommended_images: num(form.recommended_images), maximum_images: maximum,
      image_slot_names: Array.from({ length: maximum }, (_, i) => `${form.slot_prefix}${String(i + 1).padStart(2, "0")}`),
      music_enabled: form.music_enabled, beat_sync_enabled: form.beat_sync_enabled, default_duration: parseFloat(form.default_duration) || 15,
      unused_slots: form.unused_slots, fit_to_length: form.fit_to_length,
      active: form.active, preview_image: form.preview_image || null, preview_video: form.preview_video || null,
    });
  };
  return (
    <div className={`slt-tpl ${t.connected ? "is-connected" : ""}`} data-testid={`social-lite-tpl-${t.template_id}`}>
      <div className="slt-tpl-top">
        <input className="slt-tpl-name" value={form.template_name} onChange={(e) => set("template_name", e.target.value)} data-testid={`social-lite-tpl-name-${t.template_id}`} />
        <span className={`slt-tpl-state ${t.connected ? "is-on" : ""}`} data-testid={`social-lite-tpl-state-${t.template_id}`}>{t.connected ? <><Check size={11} /> Connected</> : "Not connected"}</span>
      </div>
      <label className="slt-tpl-field"><Link2 size={12} /> Creatomate Template ID
        <input value={form.creatomate_template_id} onChange={(e) => set("creatomate_template_id", e.target.value)} placeholder="Paste the template ID from Creatomate" data-testid={`social-lite-tpl-cid-${t.template_id}`} />
      </label>
      <div className="slt-tpl-counts">
        <label>Min<input type="number" min="1" value={form.minimum_images} onChange={(e) => set("minimum_images", e.target.value)} data-testid={`social-lite-tpl-min-${t.template_id}`} /></label>
        <label>Recommended<input type="number" min="1" value={form.recommended_images} onChange={(e) => set("recommended_images", e.target.value)} data-testid={`social-lite-tpl-rec-${t.template_id}`} /></label>
        <label>Max (slots)<input type="number" min="1" value={form.maximum_images} onChange={(e) => set("maximum_images", e.target.value)} data-testid={`social-lite-tpl-max-${t.template_id}`} /></label>
        <label>Slot prefix<input value={form.slot_prefix} onChange={(e) => set("slot_prefix", e.target.value)} data-testid={`social-lite-tpl-prefix-${t.template_id}`} /></label>
        <label>Target length (s)<input type="number" step="0.5" value={form.default_duration} onChange={(e) => set("default_duration", e.target.value)} data-testid={`social-lite-tpl-duration-${t.template_id}`} /></label>
      </div>
      <small className="slt-tpl-slots">Slots: {form.slot_prefix}01 → {form.slot_prefix}{String(num(form.maximum_images)).padStart(2, "0")}</small>
      <div className="slt-tpl-flags">
        <label><input type="checkbox" checked={form.fit_to_length} onChange={(e) => set("fit_to_length", e.target.checked)} data-testid={`social-lite-tpl-fit-${t.template_id}`} /> Stretch template timing to the target length (Beat Map off)</label>
        <label><input type="checkbox" checked={form.music_enabled} onChange={(e) => set("music_enabled", e.target.checked)} data-testid={`social-lite-tpl-music-${t.template_id}`} /> Music (uses the template's Music slot, else added as an audio track)</label>
        <label><input type="checkbox" checked={form.beat_sync_enabled} onChange={(e) => set("beat_sync_enabled", e.target.checked)} data-testid={`social-lite-tpl-beat-${t.template_id}`} /> Send BPM / Beat_Interval placeholders</label>
        <label><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid={`social-lite-tpl-active-${t.template_id}`} /> Active</label>
        <label>Fewer photos than slots
          <select value={form.unused_slots} onChange={(e) => set("unused_slots", e.target.value)} data-testid={`social-lite-tpl-unused-${t.template_id}`}>
            <option value="trim">Trim unused slots (shorter video)</option>
            <option value="repeat">Repeat photos to fill every slot</option>
          </select>
        </label>
      </div>
      <div className="slt-tpl-previews">
        <input value={form.preview_image} onChange={(e) => set("preview_image", e.target.value)} placeholder="Preview image URL (optional)" data-testid={`social-lite-tpl-preview-img-${t.template_id}`} />
        <input value={form.preview_video} onChange={(e) => set("preview_video", e.target.value)} placeholder="Preview video URL (optional)" data-testid={`social-lite-tpl-preview-vid-${t.template_id}`} />
      </div>
      <button className="slt-mini is-primary" onClick={submit} disabled={saving} data-testid={`social-lite-tpl-save-${t.template_id}`}>{saving ? <Loader2 size={12} className="slt-spin" /> : <Check size={12} />} Save</button>
    </div>
  );
}
